import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "@/components/landing/Reveal";
import { Footer } from "@/components/landing/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  "w-full rounded-sm border border-[#1B1B1B] bg-[#080808] px-4 py-3 text-sm text-white placeholder-[#5a5a5a] transition-colors focus:border-[#C9A45C] focus:outline-none";

export default function ThankYouPage() {
  useEffect(() => {
    document.title = "Benvenuto in Tempio Vivo | Tempio Vivo";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex";
    meta.id = "noindex-meta";
    document.head.appendChild(meta);
    return () => document.getElementById("noindex-meta")?.remove();
  }, []);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    plan: "app",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        plan: form.plan,
        message: form.message.trim() || null,
      });
      setSent(true);
      toast.success("Recapiti inviati. Ti contatteremo presto.");
    } catch {
      toast.error("Invio non riuscito. Riprova tra poco.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main
      data-testid="thank-you-page"
      className="flex min-h-screen flex-col bg-[#080808]"
    >
      <div className="mx-auto flex w-full max-w-2xl flex-1 flex-col justify-center px-6 py-20">
        <Reveal>
          <p className="text-center text-xs font-bold uppercase tracking-[0.35em] text-[#C9A45C]">
            Tempio Vivo — Fede in Azione
          </p>
          <h1
            data-testid="thank-you-heading"
            className="font-display mt-6 text-center text-4xl font-semibold leading-tight text-white sm:text-5xl"
          >
            Benvenuto in Tempio Vivo.
            <br />
            Hai appena compiuto{" "}
            <span className="text-[#C9A45C]">il primo passo</span>.
          </h1>
          <p className="mt-6 text-center text-base leading-relaxed text-[#B8B8B8]">
            Il nostro team ti contatterà per accompagnarti nell'attivazione del
            tuo percorso. Lasciaci i tuoi recapiti qui sotto per rendere tutto
            più semplice e veloce.
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          {sent ? (
            <div
              data-testid="lead-success-message"
              className="mt-12 rounded-sm border border-[#C9A45C]/40 bg-[#121212] p-8 text-center"
            >
              <p className="font-display text-2xl font-semibold text-[#C9A45C]">
                Recapiti ricevuti.
              </p>
              <p className="mt-3 text-sm leading-relaxed text-[#B8B8B8]">
                Elio o un coach di Fede &amp; Fitness Italia ti contatterà per
                attivare il tuo accesso. Dietro Tempio Vivo ci sono persone
                reali.
              </p>
            </div>
          ) : (
            <form
              data-testid="lead-form"
              onSubmit={submit}
              className="mt-12 space-y-4 rounded-sm border border-[#1B1B1B] bg-[#121212] p-6 sm:p-8"
            >
              <div>
                <label
                  htmlFor="lead-name"
                  className="mb-2 block text-xs font-bold uppercase tracking-widest text-[#B8B8B8]"
                >
                  Nome e cognome
                </label>
                <input
                  id="lead-name"
                  data-testid="lead-name-input"
                  required
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Il tuo nome"
                  className={inputClass}
                />
              </div>
              <div>
                <label
                  htmlFor="lead-email"
                  className="mb-2 block text-xs font-bold uppercase tracking-widest text-[#B8B8B8]"
                >
                  Email
                </label>
                <input
                  id="lead-email"
                  data-testid="lead-email-input"
                  type="email"
                  required
                  value={form.email}
                  onChange={update("email")}
                  placeholder="la-tua@email.it"
                  className={inputClass}
                />
              </div>
              <div>
                <label
                  htmlFor="lead-phone"
                  className="mb-2 block text-xs font-bold uppercase tracking-widest text-[#B8B8B8]"
                >
                  Telefono / WhatsApp
                </label>
                <input
                  id="lead-phone"
                  data-testid="lead-phone-input"
                  type="tel"
                  required
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="+39 ..."
                  className={inputClass}
                />
              </div>
              <div>
                <label
                  htmlFor="lead-plan"
                  className="mb-2 block text-xs font-bold uppercase tracking-widest text-[#B8B8B8]"
                >
                  Piano scelto
                </label>
                <select
                  id="lead-plan"
                  data-testid="lead-plan-select"
                  value={form.plan}
                  onChange={update("plan")}
                  className={inputClass}
                >
                  <option value="app">Tempio Vivo App — 9,99 €/mese</option>
                  <option value="community">
                    Community Coaching — 49 €/mese
                  </option>
                  <option value="non_so">Non sono sicuro</option>
                </select>
              </div>
              <div>
                <label
                  htmlFor="lead-message"
                  className="mb-2 block text-xs font-bold uppercase tracking-widest text-[#B8B8B8]"
                >
                  Messaggio (facoltativo)
                </label>
                <textarea
                  id="lead-message"
                  data-testid="lead-message-input"
                  rows={3}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="C'è qualcosa che vuoi farci sapere?"
                  className={inputClass}
                />
              </div>
              <button
                data-testid="lead-submit-button"
                type="submit"
                disabled={sending}
                className="w-full rounded-sm bg-[#C9A45C] px-8 py-4 text-xs font-bold uppercase tracking-widest text-[#080808] transition-colors duration-300 hover:bg-[#E8D5A3] disabled:opacity-60 sm:text-sm"
              >
                {sending ? "Invio in corso..." : "Invia i tuoi recapiti"}
              </button>
            </form>
          )}
        </Reveal>

        <Reveal delay={0.25}>
          <div className="mt-10 text-center">
            <a
              data-testid="thank-you-home-link"
              href="/"
              className="text-xs uppercase tracking-widest text-[#8F7138] underline-offset-4 transition-colors hover:text-[#C9A45C] hover:underline"
            >
              Torna alla pagina principale
            </a>
          </div>
        </Reveal>
      </div>
      <Footer />
    </main>
  );
}
